import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { MerchantListItem } from '@/graphql/queries/merchants';
import { useAuth } from './AuthContext';
import { readImpersonation, writeImpersonation } from '@/lib/auth-session';

interface ImpersonationContextType {
  selectedMerchant: MerchantListItem | null;
  setSelectedMerchant: (merchant: MerchantListItem | null) => void;
  impersonatedMerchantCode: string | null;
  impersonatedMerchantId: string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantListItem | null>(() => {
    const stored = readImpersonation();
    return stored ? JSON.parse(stored) : null;
  });
  const [prevAuthenticated, setPrevAuthenticated] = useState(isAuthenticated);

  if (prevAuthenticated !== isAuthenticated) {
    setPrevAuthenticated(isAuthenticated);
    if (!isAuthenticated && selectedMerchant) {
      setSelectedMerchant(null);
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      writeImpersonation(null);
      return;
    }
    writeImpersonation(selectedMerchant ? JSON.stringify(selectedMerchant) : null);
  }, [isAuthenticated, selectedMerchant]);

  const impersonatedMerchantCode = selectedMerchant?.merchantCode || null;
  const impersonatedMerchantId = selectedMerchant?.id || null;

  const contextValue = useMemo<ImpersonationContextType>(
    () => ({
      selectedMerchant,
      setSelectedMerchant,
      impersonatedMerchantCode,
      impersonatedMerchantId,
    }),
    [selectedMerchant, impersonatedMerchantCode, impersonatedMerchantId],
  );

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
